/**
 * SF-101: Frontend Privilege Escalation Prevention
 *
 * This utility filters the user type dropdown to only show types
 * the current user is allowed to assign. This is defense-in-depth;
 * the backend also enforces these restrictions.
 *
 * Privilege Hierarchy (highest to lowest):
 *   supreme_admin (100) > super_admin (90) > global_admin (70) >
 *   admin (55) > location_admin (50) > web_and_content_editor (30) >
 *   instructor (20) > customer (10)
 */

import { UserType } from '../services/endpoints/people/user';

/** Roles that operate across all locations (see isGlobal in utils/consts). */
const GLOBAL_USER_TYPES: UserType[] = ['supreme_admin', 'super_admin', 'global_admin'];

export const isGlobalUserType = (userType: UserType | undefined): boolean =>
    userType !== undefined && GLOBAL_USER_TYPES.includes(userType);

/**
 * Privilege levels for each user type.
 * Higher number = more privileges.
 */
export const PRIVILEGE_LEVELS: Record<UserType, number> = {
    supreme_admin: 100,
    super_admin: 90,
    global_admin: 70,
    admin: 55,
    location_admin: 50,
    web_and_content_editor: 30,
    instructor: 20,
    customer: 10,
};

/**
 * Map user_type_id to user_type string
 * Based on user_types table in database
 */
export const USER_TYPE_ID_MAP: Record<number, UserType> = {
    0: 'customer',
    1: 'instructor',
    2: 'admin',
    3: 'global_admin',
    4: 'super_admin',
    5: 'supreme_admin',
    6: 'web_and_content_editor',
    7: 'location_admin',
};

/**
 * Map a UserType to the legacy admin_level string expected by create/update SFC APIs.
 * RBAC uses user_type_id; admin_level is still required by the API schema.
 */
export const userTypeToAdminLevel = (userType: UserType | string): string => {
    switch (userType) {
        case 'customer':
            return 'parent';
        case 'instructor':
            return 'instructor';
        case 'super_admin':
            return 'super-admin';
        case 'supreme_admin':
            return 'supreme-admin';
        default:
            // admin, location_admin, global_admin, web_and_content_editor
            return 'admin';
    }
};

/**
 * Get the user_type string from either user_type or user_type_id
 */
export const getUserTypeFromUser = (user: { user_type?: UserType; user_type_id?: number } | null): UserType | undefined => {
    if (!user) return undefined;

    // If user_type string is available and valid, use it
    if (user.user_type && PRIVILEGE_LEVELS[user.user_type] !== undefined) {
        return user.user_type;
    }

    // Otherwise, convert from user_type_id
    if (user.user_type_id && USER_TYPE_ID_MAP[user.user_type_id]) {
        return USER_TYPE_ID_MAP[user.user_type_id];
    }

    return undefined;
};

/**
 * Check if a user with the given role can assign a target user type.
 * Users can only assign types at or below their own privilege level.
 *
 * @param requesterUserType - The user type of the person making the request
 * @param targetUserType - The user type being assigned
 * @returns true if the assignment is allowed
 */
export const canAssignUserType = (
    requesterUserType: UserType | undefined,
    targetUserType: UserType
): boolean => {
    if (!requesterUserType) return false;

    const requesterLevel = PRIVILEGE_LEVELS[requesterUserType] ?? 0;
    const targetLevel = PRIVILEGE_LEVELS[targetUserType] ?? 0;

    return requesterLevel >= targetLevel;
};

/**
 * Get the list of user types that a user can assign.
 *
 * @param requesterUserType - The user type of the person making the request
 * @returns Array of user types the requester can assign
 */
export const getAssignableUserTypes = (
    requesterUserType: UserType | undefined
): UserType[] => {
    if (!requesterUserType) return [];

    const requesterLevel = PRIVILEGE_LEVELS[requesterUserType] ?? 0;
    const requesterIsGlobal = isGlobalUserType(requesterUserType);

    return (Object.keys(PRIVILEGE_LEVELS) as UserType[]).filter((userType) => {
        // Web and Content Editor is a global-only assignable role
        if (userType === 'web_and_content_editor' && !requesterIsGlobal) {
            return false;
        }
        return PRIVILEGE_LEVELS[userType] <= requesterLevel;
    });
};

/**
 * Filter userTypeNames object to only include assignable types.
 * Use this to filter the dropdown options.
 *
 * @param userTypeNames - The full userTypeNames object from consts
 * @param requesterUserType - The user type of the person making the request
 * @returns Filtered object with only assignable user types
 */
export const filterAssignableUserTypeNames = <T extends Record<string, string>>(
    userTypeNames: T,
    requesterUserType: UserType | undefined
): Partial<T> => {
    if (!requesterUserType) return {};

    const assignable = getAssignableUserTypes(requesterUserType);

    return Object.fromEntries(
        Object.entries(userTypeNames).filter(([key]) =>
            assignable.includes(key as UserType)
        )
    ) as Partial<T>;
};
